/**
 * Editing a task from anywhere (plan, task list): what undoes a change, and
 * the next free number. Pure, tested.
 */

/** What a task looks like for undoing: the stored fields, and a pinned start. */
export interface TaskState {
  durationDays: number;
  constraintType: string | null;
  constraintDate: string | null;
}

/**
 * The body that puts a task back as it was before `body`. The shortcuts
 * `startDate` and `finishDate` are undone through what they changed: the
 * pinned start, and the duration.
 */
export function inverseTaskBody(task: TaskState, body: Record<string, unknown>): Record<string, unknown> {
  const back: Record<string, unknown> = {};
  for (const key of Object.keys(body)) {
    if (key === 'startDate') back.startDate = task.constraintType === 'startNoEarlierThan' ? task.constraintDate : null;
    else if (key === 'finishDate') back.durationDays = task.durationDays;
    else if (key in task) back[key] = (task as unknown as Record<string, unknown>)[key];
  }
  // Reopening forgets the day it was done; undoing brings that day back, not today.
  const t = task as unknown as { status?: string; doneOn?: string | null };
  if ('status' in body && t.status === 'done' && t.doneOn) back.doneOn = t.doneOn;
  return back;
}

/** A number for a new task: the next ten above the highest whole number in use ("40" after "10", "20", "35"), else "". */
export function nextTaskCode(codes: readonly string[]): string {
  const numbers = codes.filter((c) => /^\d+$/.test(c)).map(Number);
  if (numbers.length === 0) return codes.length === 0 ? '10' : '';
  const max = Math.max(...numbers);
  let next = (Math.floor(max / 10) + 1) * 10;
  while (codes.includes(String(next))) next += 10;
  return String(next);
}
